import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import {
  Add,
  Upload,
  Search,
  MoreVert,
  Edit,
  Delete,
  CalendarToday,
  Business,
  ContentCopy,
  Event,
  Download
} from '@mui/icons-material';
import dayjs from 'dayjs';

const HolidayPlanner = ({
  holidays = [],
  onCreateCollection,
  onUpdateCollection,
  onDeleteCollection,
  onImportCollection,
  onBulkUpdateCollection
}) => {
  // --- State ---
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState(''); // Default to empty string initially
  const [activeTab, setActiveTab] = useState(0); // 0: Year View, 1: My Collections
  const [selectedYear, setSelectedYear] = useState(dayjs().year()); // Year selector for Year View
  const [collectionsSubTab, setCollectionsSubTab] = useState(0); // 0: Year Holidays, 1: Saved Lists

  // Dialog State
  const [openHolidayDialog, setOpenHolidayDialog] = useState(false);
  const [openBulkUpdateDialog, setOpenBulkUpdateDialog] = useState(false);
  const [bulkYear, setBulkYear] = useState(dayjs().year() + 1);
  const [bulkInputs, setBulkInputs] = useState({}); // { holidayName: newDateStr }
  const [bulkSelected, setBulkSelected] = useState({}); // { holidayName: boolean }

  // Form State
  const [formDate, setFormDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formFloating, setFormFloating] = useState(false);
  const [formRepeat, setFormRepeat] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [dialogCollectionId, setDialogCollectionId] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const [expandedCollections, setExpandedCollections] = useState({}); // Manual expansion state

  // Custom Dialog State
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    type: 'alert', // 'alert' | 'confirm' | 'prompt'
    title: '',
    message: '',
    onConfirm: null,
    inputValue: '',
    confirmLabel: ''
  });

  // --- Effects ---
  useEffect(() => {
    if (Array.isArray(holidays)) {
      setCollections(holidays);
      // Only set default if nothing selected yet
      if (holidays.length > 0 && !selectedCollectionId) {
        const firstActive = holidays.find(c => !c.isDeleted);
        if (firstActive) setSelectedCollectionId(firstActive._id || '');
      } else if (holidays.length === 0) {
        setSelectedCollectionId(''); // Clear selection if no collections
      }
    }
  }, [holidays, selectedCollectionId]);

  useEffect(() => {
    if (selectedDate) {
      setFormDate(selectedDate.format('YYYY-MM-DD'));
    }
  }, [selectedDate]);

  // Sync bulk inputs when target year changes to ensure dates match the selected year
  useEffect(() => {
    setExpandedCollections({});
  }, [searchQuery]);

  useEffect(() => {
    if (openBulkUpdateDialog) {
      const floatingNames = [...new Set(collections
        .filter(c => !c.isDeleted)
        .flatMap(c => c.holidays || [])
        .filter(h => !h.isDeleted && h.isFloating)
        .map(h => h.name)
      )];

      const newInputs = {};
      const newSelected = {};
      floatingNames.forEach(name => {
        newSelected[name] = true; // Default to selected
        // Find if this holiday EXISTS in the target year already
        const inYear = collections
          .filter(c => !c.isDeleted)
          .flatMap(c => c.holidays || [])
          .find(h => !h.isDeleted && h.name === name && dayjs(h.date).year() === parseInt(bulkYear));

        if (inYear) {
          newInputs[name] = dayjs(inYear.date).format('YYYY-MM-DD');
        } else {
          // Fallback to latest known date shifted to this year
          const latest = collections
            .filter(c => !c.isDeleted)
            .flatMap(c => c.holidays || [])
            .filter(h => !h.isDeleted && h.name === name)
            .sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix())[0];

          if (latest) {
            newInputs[name] = dayjs(latest.date).year(bulkYear).format('YYYY-MM-DD');
          }
        }
      });
      setBulkInputs(newInputs);
      setBulkSelected(newSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkYear, openBulkUpdateDialog]);

  // --- Handlers ---

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Helper to strip IDs and deleted status from holidays for "clean" export/import
  const getCleanHolidays = (hols) => {
    return (hols || [])
      .filter(h => !h.isDeleted)
      .map(({ _id, date, name, description, isFloating, isRepeat }) => ({
        _id,
        date,
        name,
        description,
        isFloating: !!isFloating,
        isRepeat: !!isRepeat,
        Add: 'no',
        Update: 'yes',
        Delete: 'no'
      }));
  };

  // Helper to strip IDs from collections
  const getCleanCollections = (cols) => {
    return (cols || [])
      .filter(c => !c.isDeleted)
      .map(c => ({
        _id: c._id,
        name: c.name,
        active: c.active,
        holidays: getCleanHolidays(c.holidays)
      }));
  };

  const handleOpenDialog = (config) => {
    setDialogConfig({
      open: true,
      title: config.title || (config.type === 'confirm' ? 'Confirmation' : config.type === 'prompt' ? 'Input Required' : 'Notification'),
      message: config.message || '',
      type: config.type || 'alert',
      onConfirm: config.onConfirm || null,
      inputValue: config.defaultValue || '',
      confirmLabel: config.confirmLabel || ''
    });
  };

  const handleCloseDialog = () => {
    setDialogConfig(prev => ({ ...prev, open: false }));
  };

  const handleDialogConfirm = () => {
    const { onConfirm, type, inputValue } = dialogConfig;
    handleCloseDialog();
    if (onConfirm) {
      // Use setTimeout to ensure the previous dialog state is cleared before potentially opening a new one
      setTimeout(() => {
        onConfirm(type === 'prompt' ? inputValue : true);
      }, 100);
    }
  };

  const handleAddClick = (collectionId, specificDate) => {
    const activeCollections = collections.filter(c => !c.isDeleted);
    const isSpecialCall = typeof collectionId === 'string';

    if (activeCollections.length === 0 && !isSpecialCall) {
      handleOpenDialog({
        type: 'confirm',
        title: 'No Collections Found',
        message: 'You must create a collection before adding holidays. Create your first collection now?',
        confirmLabel: 'Add',
        onConfirm: () => {
          setActiveTab(1); // Navigate to Collections tab
          setTimeout(() => {
            handleCreateCollection((newId) => {
              // After creation, trigger the add holiday dialog for this new collection
              handleAddClick(newId, specificDate);
            });
          }, 100); // Small delay to allow tab transition to be visible if needed
        }
      });
      return;
    }

    // Use specificDate if provided, otherwise fallback to selectedDate
    setFormDate(specificDate || selectedDate.format('YYYY-MM-DD'));
    setFormName('');
    setFormDesc('');
    setFormFloating(false);
    setFormRepeat(false);
    setEditingHoliday(null);
    // If collectionId is an event object (from direct onClick), treat as empty string
    const targetId = (typeof collectionId === 'string') ? collectionId : '';
    setDialogCollectionId(targetId || activeCollections[0]?._id || '');
    setOpenHolidayDialog(true);
  }

  const handleEditClick = (holiday) => {
    setFormDate(holiday.date);
    setFormName(holiday.name);
    setFormDesc(holiday.description || '');
    setFormFloating(!!holiday.isFloating);
    setFormRepeat(!!holiday.isRepeat);
    setEditingHoliday(holiday);
    // Find owner collection
    const ownerCol = collections.find(c => c.isDeleted !== true && c.holidays?.some(h => h.isDeleted !== true && h.date === holiday.date && h.name === holiday.name));
    setDialogCollectionId(ownerCol?._id || '');
    setOpenHolidayDialog(true);
  }

  const handleSaveHoliday = () => {
    if (!formName || !formDate || !dialogCollectionId) {
      handleOpenDialog({
        type: 'alert',
        title: 'Validation Error',
        message: 'Please select a collection and fill required fields.'
      });
      return;
    }

    const todayStr = dayjs().format('YYYY-MM-DD');
    if (dayjs(formDate).isBefore(todayStr, 'day')) {
      handleOpenDialog({
        type: 'alert',
        title: 'Validation Error',
        message: 'You cannot select a past date for a holiday.'
      });
      return;
    }

    // Duplicate Check
    const existingGlobalHoliday = collections
      .filter(c => c.isDeleted !== true)
      .flatMap(c => c.holidays || [])
      .find(h => h.isDeleted !== true && h.date === formDate);

    if (existingGlobalHoliday) {
      const isSameInstance = editingHoliday &&
        existingGlobalHoliday.date === editingHoliday.date &&
        existingGlobalHoliday.name === editingHoliday.name;

      if (!isSameInstance) {
        handleOpenDialog({
          type: 'alert',
          title: 'Duplicate Holiday',
          message: `ERROR: The date ${formDate} is already booked for "${existingGlobalHoliday.name}". Please choose another date.`
        });
        return;
      }
    }

    const newHoliday = {
      date: formDate,
      name: formName.trim(),
      description: (formDesc || "").trim(),
      isFloating: formFloating,
      isRepeat: formRepeat,
      isDeleted: false,
      createdAt: editingHoliday?.createdAt || new Date().toISOString()
    };

    if (editingHoliday && editingHoliday._id) {
      newHoliday._id = editingHoliday._id;
    }

    if (editingHoliday) {
      // Find original owner
      const originalOwner = collections.find(c => c.holidays?.some(h => h.date === editingHoliday.date && h.name === editingHoliday.name));
      const targetCol = collections.find(c => c._id === dialogCollectionId);

      if (originalOwner && targetCol && originalOwner._id !== dialogCollectionId) {
        // MOVED: Update BOTH
        if (onUpdateCollection) {
          // 1. Remove from original
          const updatedOriginalHols = originalOwner.holidays.filter(h => !(h.date === editingHoliday.date && h.name === editingHoliday.name));
          onUpdateCollection({ _id: originalOwner._id, holidays: updatedOriginalHols });

          // 2. Add to new
          const updatedTargetHols = [...(targetCol.holidays || []), newHoliday];
          setTimeout(() => {
            onUpdateCollection({ _id: targetCol._id, holidays: updatedTargetHols });
          }, 200);
        }
      } else if (targetCol) {
        // SAME Collection Update
        const hIdx = (targetCol.holidays || []).findIndex(h => h.date === editingHoliday.date && h.name === editingHoliday.name);
        if (hIdx !== -1 && onUpdateCollection) {
          const updatedHols = [...(targetCol.holidays || [])];
          updatedHols[hIdx] = { ...updatedHols[hIdx], ...newHoliday };
          onUpdateCollection({ _id: targetCol._id, holidays: updatedHols });
        }
      }
    } else {
      // NEW Holiday
      const targetCol = collections.find(c => c._id === dialogCollectionId);
      if (targetCol) {
        const exists = (targetCol.holidays || []).some(h => h.date === formDate);
        if (exists) {
          handleOpenDialog({
            type: 'confirm',
            message: "Overwrite existing holiday on this date?",
            onConfirm: () => {
              if (onUpdateCollection) {
                const updatedHols = (targetCol.holidays || []).filter(h => h.date !== formDate);
                updatedHols.push(newHoliday);
                onUpdateCollection({ _id: targetCol._id, holidays: updatedHols });
              }
              setOpenHolidayDialog(false);
            }
          });
          return;
        }
        if (onUpdateCollection) {
          const updatedHols = [...(targetCol.holidays || []), newHoliday];
          onUpdateCollection({ _id: targetCol._id, holidays: updatedHols });
        }
      }
    }
    setOpenHolidayDialog(false);
  };

  const handleCreateCollection = (callback) => {
    handleOpenDialog({
      type: 'prompt',
      title: 'New Collection',
      message: 'Enter collection name:',
      onConfirm: async (name) => {
        if (!name) return;
        if (onCreateCollection) {
          try {
            // Assuming the parent action returns the response
            const res = await onCreateCollection({ name: name.trim() });
            if (res && res.data && res.data.data) {
              if (callback && typeof callback === 'function') {
                callback(res.data.data._id);
              }
            }
          } catch (e) {
            console.error("Failed to create collection", e);
          }
        }
      }
    });
  }

  const handleDeleteCollection = (id) => {
    handleOpenDialog({
      type: 'confirm',
      title: 'Delete Collection',
      message: 'Are you sure you want to delete this entire collection?',
      onConfirm: () => {
        if (onDeleteCollection) {
          onDeleteCollection({
            _id: id
          });
        }
      }
    });
  }

  const handleRenameCollection = (id, currentName) => {
    handleOpenDialog({
      type: 'prompt',
      title: 'Rename Collection',
      message: 'Enter new name:',
      defaultValue: currentName,
      onConfirm: (newName) => {
        if (!newName) return;
        if (onUpdateCollection) {
          onUpdateCollection({ _id: id, name: newName.trim() });
        }
      }
    });
  }

  const handleDeleteHoliday = (holiday) => {
    handleOpenDialog({
      type: 'confirm',
      title: 'Delete Holiday',
      message: `Are you sure you want to delete "${holiday.name}"?`,
      onConfirm: () => {
        collections.forEach((col) => {
          const hIdx = (col.holidays || []).findIndex(h =>
            (h._id && holiday._id && h._id === holiday._id) ||
            (h.date === holiday.date && h.name === holiday.name)
          );
          if (hIdx !== -1 && onUpdateCollection) {
            const updatedHols = [...col.holidays];
            updatedHols[hIdx] = {
              ...updatedHols[hIdx],
              isDeleted: true,
              deletedAt: new Date().toISOString()
            };
            onUpdateCollection({ _id: col._id, holidays: updatedHols });
          }
        });
      }
    });
  }

  const handleImportJSON = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);

        // Helper to process a list of holidays against an existing list
        const processHolidays = (existingHolidays, newHolidays) => {
          const merged = [...(existingHolidays || [])];

          newHolidays.forEach(h => {
            const matchIndex = merged.findIndex(mh => mh.date === h.date && !mh.isDeleted);

            if (h.Delete === 'yes' || h.Delete === true) {
              if (matchIndex !== -1) {
                merged[matchIndex] = { ...merged[matchIndex], isDeleted: true };
              }
            } else if (h.Update === 'yes' || h.Update === true) {
              if (matchIndex !== -1) {
                merged[matchIndex] = {
                  ...merged[matchIndex],
                  name: h.name || merged[matchIndex].name,
                  description: h.description !== undefined ? h.description : merged[matchIndex].description,
                  isFloating: h.isFloating !== undefined ? h.isFloating : merged[matchIndex].isFloating,
                  isRepeat: h.isRepeat !== undefined ? h.isRepeat : merged[matchIndex].isRepeat
                };
              }
            } else {
              if (matchIndex === -1 && h.Add !== 'no' && h.Add !== false) {
                merged.push({
                  ...h,
                  _id: undefined,
                  date: dayjs(h.date).format('YYYY-MM-DD'),
                  isDeleted: false
                });
              }
            }
          });
          return merged;
        };

        const isCollectionList = Array.isArray(importedData) && importedData.length > 0 && Array.isArray(importedData[0].holidays);

        if (isCollectionList && onBulkUpdateCollection) {
          handleOpenDialog({
            type: 'confirm',
            title: 'Import Collections (Bulk)',
            message: `Merge ${importedData.length} collections? This will be processed in one go.`,
            onConfirm: async () => {
              // 1. Prepare Bulk Data locally
              const bulkPayload = importedData.map(impCol => {
                let existingCol = null;
                if (impCol._id) existingCol = collections.find(c => c._id === impCol._id && !c.isDeleted);
                if (!existingCol && impCol.name) existingCol = collections.find(c => c.name === impCol.name && !c.isDeleted);

                let finalHolidays = impCol.holidays || [];
                if (existingCol) {
                  finalHolidays = processHolidays(existingCol.holidays, impCol.holidays || []);
                } else {
                  finalHolidays = (impCol.holidays || []).filter(h => h.Delete !== 'yes' && h.Delete !== true).map(h => ({
                    ...h,
                    date: dayjs(h.date).format('YYYY-MM-DD'),
                    isDeleted: false
                  }));
                }

                return {
                  _id: existingCol ? existingCol._id : undefined,
                  name: (impCol.name && existingCol && impCol.name !== existingCol.name) ? impCol.name : (existingCol ? existingCol.name : impCol.name),
                  description: impCol.description,
                  holidays: finalHolidays
                };
              });

              // 2. Call Bulk API
              try {
                const resultData = await onBulkUpdateCollection({ collections: bulkPayload });
                if (resultData && resultData.results) {
                  const failures = resultData.results.filter(r => r.status !== 'success');
                  if (failures.length > 0) {
                    const failMsg = failures.map(f => `• ${f.name}: ${f.error}`).join('\n');
                    handleOpenDialog({
                      type: 'alert',
                      title: 'Import Partial Success',
                      message: `Some collections failed to import:\n${failMsg}`
                    });
                  } else {
                    handleOpenDialog({ type: 'alert', title: 'Success', message: 'All collections imported successfully.' });
                  }
                }
              } catch (err) {
                console.error(err);
                handleOpenDialog({ type: 'alert', title: 'Error', message: 'Bulk import failed. Please check console.' });
              }
            }
          });
          return;
        }

        // Fallback for Single List or if Bulk API not available
        if (isCollectionList) {
          // Old Sequential Fallback (Should typically not be reached if onBulkUpdateCollection is wired)
          handleOpenDialog({
            type: 'confirm',
            title: 'Import Collections',
            message: `Merge ${importedData.length} collections? This will process Add/Update/Delete commands.`,
            onConfirm: async () => {
              const operations = importedData.map(impCol => {
                let existingCol = null;
                if (impCol._id) {
                  existingCol = collections.find(c => c._id === impCol._id && !c.isDeleted);
                }
                if (!existingCol && impCol.name) {
                  existingCol = collections.find(c => c.name === impCol.name && !c.isDeleted);
                }
                if (existingCol) {
                  if (onUpdateCollection) {
                    const mergedHolidays = processHolidays(existingCol.holidays, impCol.holidays || []);
                    const updatedCol = {
                      _id: existingCol._id,
                      name: (impCol.name && impCol.name !== existingCol.name) ? impCol.name : existingCol.name,
                      holidays: mergedHolidays
                    };
                    return onUpdateCollection(updatedCol);
                  }
                } else {
                  const validHolidays = (impCol.holidays || []).filter(h => h.Delete !== 'yes' && h.Delete !== true).map(h => ({
                    ...h,
                    date: dayjs(h.date).format('YYYY-MM-DD'),
                    isDeleted: false
                  }));
                  if (onCreateCollection) {
                    return onCreateCollection({
                      name: impCol.name || "Imported Collection",
                      description: impCol.description,
                      holidays: validHolidays
                    });
                  }
                }
                return Promise.resolve();
              });

              try {
                await Promise.all(operations);
                handleOpenDialog({ type: 'alert', title: 'Success', message: 'Import processed.' });
              } catch (error) {
                console.error('Import error:', error);
                handleOpenDialog({ type: 'alert', title: 'Error', message: 'Some imports failed.' });
              }
            }
          });
        } else {
          // Flattened single list
          const importedHolidays = Array.isArray(importedData) ? importedData : (importedData.holidays || []);
          handleOpenDialog({
            type: 'prompt',
            title: 'Import to Collection',
            message: 'Enter collection name to import into:',
            defaultValue: file.name.replace('.json', ''),
            onConfirm: (name) => {
              const existingCol = collections.find(c => c.name === name && !c.isDeleted);

              if (existingCol) {
                if (onUpdateCollection) {
                  const mergedHolidays = processHolidays(existingCol.holidays, importedHolidays);
                  onUpdateCollection({ _id: existingCol._id, holidays: mergedHolidays });
                }
              } else {
                // Create New
                const validHolidays = importedHolidays
                  .filter(h => h.Delete !== 'yes' && h.Delete !== true)
                  .map(h => ({
                    ...h,
                    date: dayjs(h.date).format('YYYY-MM-DD'),
                    isDeleted: false
                  }));

                if (onCreateCollection) {
                  onCreateCollection({
                    name: name,
                    holidays: validHolidays
                  });
                }
              }
            }
          });
        }
      } catch (err) {
        handleOpenDialog({ type: 'alert', title: 'Import Error', message: "Invalid JSON: " + err.message });
      }
    };
    reader.readAsText(file);
    event.target.value = null;
  }

  const handleDownloadJSON = () => {
    const cleanData = getCleanCollections(collections);
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(cleanData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "holiday_collections.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  }

  // --- Derived Data ---
  const allGlobalHolidaysMap = collections
    .filter(c => c.isDeleted !== true)
    .flatMap(c => (c.holidays || []).filter(h => h.isDeleted !== true))
    .reduce((acc, hol) => {
      const key = `${hol.date}_${hol.name.trim().toLowerCase()}`;
      if (!acc[key]) acc[key] = hol;
      return acc;
    }, {});

  const allGlobalHolidays = Object.values(allGlobalHolidaysMap);

  // Custom Day Renderer (Memoized to prevent initialization errors)
  const ServerDay = React.useMemo(() => {
    return (props) => {
      const { day, outsideCurrentMonth, ...other } = props;
      const dateStr = day.format('YYYY-MM-DD');

      const isHoliday = allGlobalHolidays.some(h => {
        if (!h.date) return false;
        const hDate = dayjs(h.date);
        const current = day.startOf('day');

        if (hDate.isSame(current, 'day')) return true;

        if (h.isRepeat) {
          // Check if month and day match, regardless of year
          return hDate.month() === current.month() && hDate.date() === current.date();
        }
        return false;
      });

      const handleDayClick = (event) => {
        const isPast = day.isBefore(dayjs(), 'day');
        if (isPast && !isHoliday) return;
        if (other.onClick) other.onClick(event);

        if (event.detail === 2) {
          if (isPast) return;
          event.stopPropagation();
          if (isHoliday) {
            const holiday = allGlobalHolidays.find(h => {
              const hDate = dayjs(h.date);
              const current = day.startOf('day');

              if (hDate.isSame(current, 'day')) return true;

              if (h.isRepeat) {
                return hDate.month() === current.month() && hDate.date() === current.date();
              }
              return false;
            });
            if (holiday) handleEditClick(holiday);
          } else {
            handleAddClick(null, dateStr);
          }
        }
      };

      return (
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          onClick={handleDayClick}
          sx={{
            ...(isHoliday && {
              backgroundColor: 'white !important',
              color: 'red !important',
              borderRadius: '50%',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#f5f5f5 !important' },
              '&.Mui-selected': { backgroundColor: 'white !important', color: 'red !important', border: '1px solid red' }
            }),
          }}
        />
      );
    };
  }, [allGlobalHolidays, handleAddClick, handleEditClick]);



  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, px: 1 }}>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Year-End Coverage Alert */}
          {(() => {
            const today = dayjs();
            const targetYear = today.year() + 1;
            const isYearEnd = today.month() >= 10;
            if (isYearEnd) {
              // Check if any holiday exists for the target year
              const holidaysInTargetYear = collections
                .filter(c => !c.isDeleted)
                .flatMap(c => c.holidays || [])
                .some(h => !h.isDeleted && dayjs(h.date).year() === targetYear);

              if (!holidaysInTargetYear) {
                return (
                  <Alert severity="warning" sx={{ py: 0, fontWeight: 'bold' }}>
                    Year-end approaching: Please ensure holiday coverage for {targetYear}.
                  </Alert>
                );
              }
            }
            return null;
          })()}

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Event />}
            onClick={() => setOpenBulkUpdateDialog(true)}
          >
            Update Floating Dates
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddClick}
          >
            Add Holiday
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="holiday tabs">
          <Tab label="Calendar View" />
          <Tab label="My Collections" />
        </Tabs>
      </Box>

      {/* Tab 1: Calendar View */}
      {
        activeTab === 0 && (
          <Card sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
            <CardContent>
              <Grid container spacing={4}>
                {/* Left Column: Calendar */}
                <Grid item xs={12} md={7}>
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateCalendar
                        value={selectedDate}
                        onChange={handleDateChange}
                        slots={{ day: ServerDay }}
                        disablePast
                      />
                    </LocalizationProvider>
                  </Box>
                </Grid>

                {/* Right Column: Upcoming Holidays List */}
                <Grid item xs={12} md={5}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Upcoming Holidays
                  </Typography>

                  {allGlobalHolidays.length === 0 ? (
                    <Alert severity="info" variant="outlined">No holidays found.</Alert>
                  ) : (
                    <List dense sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.default', borderRadius: 1 }}>
                      {allGlobalHolidays
                        .map(h => {
                          const hDate = dayjs(h.date);
                          const today = dayjs().startOf('day');

                          // If it's a repeating holiday and the date has already passed this year,
                          // show it for the next year in the "Upcoming" list.
                          if (h.isRepeat) {
                            let nextOccurrence = hDate.year(today.year());
                            if (nextOccurrence.isBefore(today, 'day')) {
                              nextOccurrence = nextOccurrence.add(1, 'year');
                            }
                            return { ...h, displayDate: nextOccurrence };
                          }
                          return { ...h, displayDate: hDate };
                        })
                        .filter(h => h.displayDate.isAfter(dayjs().subtract(1, 'day'))) // Upcoming only
                        .sort((a, b) => a.displayDate.valueOf() - b.displayDate.valueOf())
                        .slice(0, 5) // Limit to 5 items
                        .map((h, i) => (
                          <ListItem
                            key={h._id || i}
                            divider
                            secondaryAction={
                              <>
                                <Tooltip title="Edit Holiday">
                                  <IconButton edge="end" size="small" onClick={() => handleEditClick(h)} aria-label="edit">
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Holiday">
                                  <IconButton edge="end" size="small" onClick={() => handleDeleteHoliday(h)} aria-label="delete">
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            }
                          >
                            <Box sx={{ mr: 2, display: 'flex', flexShrink: 0, alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', width: 36, height: 36, borderRadius: '50%' }}>
                              <Event fontSize="small" />
                            </Box>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {h.name}
                                  {h.isFloating && (
                                    <Chip
                                      label="Floating"
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  )}
                                  {h.isRepeat && (
                                    <Chip
                                      label="Repeating"
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.65rem' }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={h.displayDate.format('dddd, MMMM D, YYYY')}
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                          </ListItem>
                        ))}
                      {allGlobalHolidays.filter(h => dayjs(h.date).isAfter(dayjs().subtract(1, 'day'))).length === 0 && (
                        <ListItem><ListItemText secondary="No upcoming holidays." /></ListItem>
                      )}
                    </List>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )
      }


      {/* Tab 2: My Collections with Year Report & Saved Lists side by side */}
      {
        activeTab === 1 && (
          <Grid container spacing={3}>
            {/* Left Panel: Year Report */}


            {/* Right Panel: Saved Lists */}
            <Grid item xs={12} md={7}>
              <Card sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                <CardContent>
                  {/* Header with Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Saved Lists
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <input
                        accept=".json"
                        style={{ display: 'none' }}
                        id="raised-button-file"
                        type="file"
                        onChange={handleImportJSON}
                      />
                      <label htmlFor="raised-button-file">
                        <Tooltip title="Import JSON">
                          <IconButton component="span" color="primary" size="small">
                            <Upload />
                          </IconButton>
                        </Tooltip>
                      </label>
                      <Tooltip title="Download JSON">
                        <IconButton color="primary" size="small" onClick={handleDownloadJSON}>
                          <Download />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => handleCreateCollection()}
                      >
                        New Collection
                      </Button>
                    </Box>
                  </Box>

                  {/* Search Bar */}
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Search collections or holidays..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ mb: 2 }}
                  />

                  {/* Summary Stats Row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>{collections.filter(c => !c.isDeleted).length}</strong> Collections
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Event sx={{ color: 'text.secondary', fontSize: 18 }} />
                      <Typography variant="body2" color="text.secondary">
                        <strong>
                          {collections
                            .filter(c => !c.isDeleted)
                            .reduce((sum, col) => sum + (col.holidays?.filter(h => !h.isDeleted).length || 0), 0)}
                        </strong> Total Holidays
                      </Typography>
                    </Box>
                  </Box>

                  {/* Collections List */}
                  <Box sx={{ maxHeight: 450, overflow: 'auto' }}>
                    {collections
                      .filter(c => {
                        if (c.isDeleted) return false;
                        const query = searchQuery.toLowerCase();
                        if (!query) return true;
                        const matchesCollectionName = c.name.toLowerCase().includes(query);
                        const matchesHoliday = (c.holidays || []).some(h =>
                          !h.isDeleted && (
                            h.name.toLowerCase().includes(query) ||
                            h.date.includes(query)
                          )
                        );
                        return matchesCollectionName || matchesHoliday;
                      })
                      .map(col => {
                        const query = searchQuery.toLowerCase();
                        const filteredHolidays = (col.holidays || [])
                          .filter(h => !h.isDeleted)
                          .filter(h => {
                            if (!query) return true;
                            return h.name.toLowerCase().includes(query) ||
                              h.date.includes(query) ||
                              col.name.toLowerCase().includes(query);
                          })
                          .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

                        const matchesCollectionName = col.name.toLowerCase().includes(query);
                        const matchesHoliday = (col.holidays || []).some(h => !h.isDeleted && (h.name.toLowerCase().includes(query) || h.date.includes(query)));
                        const isExpanded = expandedCollections[col._id] !== undefined ? expandedCollections[col._id] : (!!query && (matchesCollectionName || matchesHoliday));

                        return (
                          <Accordion
                            key={col._id}
                            variant="outlined"
                            sx={{ mb: 1, '&:before': { display: 'none' } }}
                            expanded={isExpanded}
                            onChange={(e, expanded) => setExpandedCollections(prev => ({ ...prev, [col._id]: expanded }))}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{
                                    bgcolor: 'primary.main',
                                    borderRadius: 1,
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <CalendarToday sx={{ fontSize: 18, color: 'primary.contrastText' }} />
                                  </Box>
                                  <Box>
                                    <Typography fontWeight="bold" variant="body2">{col.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {(col.holidays?.filter(h => h.isDeleted !== true).length || 0)} holidays
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box onClick={(e) => e.stopPropagation()}>
                                  <Tooltip title="Add Holiday">
                                    <IconButton size="small" color="primary" onClick={() => handleAddClick(col._id)}>
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Rename">
                                    <IconButton size="small" onClick={() => handleRenameCollection(col._id, col.name)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteCollection(col._id)}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <List dense disablePadding>
                                {filteredHolidays.map((h, i) => (
                                  <ListItem
                                    key={i}
                                    sx={{
                                      py: 0.5,
                                      borderBottom: i < filteredHolidays.length - 1 ? '1px solid' : 'none',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <Typography variant="body2">{h.name}</Typography>
                                          {h.isFloating && (
                                            <Chip label="Floating" size="small" color="secondary" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                          )}
                                          {h.isRepeat && (
                                            <Chip label="Repeating" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary">
                                          {dayjs(h.date).format('MMM D, YYYY')} {h.description && `• ${h.description}`}
                                        </Typography>
                                      }
                                    />
                                    <Box>
                                      <IconButton size="small" onClick={() => { setSelectedCollectionId(col._id); handleEditClick(h); }}>
                                        <Edit sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDeleteHoliday(h)}>
                                        <Delete sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  </ListItem>
                                ))}
                                {filteredHolidays.length === 0 && (
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                    No holidays in this collection.
                                  </Typography>
                                )}
                              </List>
                            </AccordionDetails>
                          </Accordion>
                        )
                      })}
                    {collections.filter(c => !c.isDeleted).length === 0 && (
                      <Alert severity="info" variant="outlined">
                        No collections yet. Click "New Collection" to create one.
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, height: '100%' }}>
                <CardContent>
                  {/* Header with Year Selector */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Year Report
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ fontWeight: 'bold' }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }} // Enable scrolling for long lists
                      >
                        {(() => {
                          const startYear = 2026;
                          const endYear = dayjs().year() + 5;
                          return Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i).map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                          ));
                        })()}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Year Holidays List */}
                  {(() => {
                    // Generate holidays for the selected year
                    const yearHolidays = [];
                    collections
                      .filter(c => {
                        // If not deleted, show the collection
                        if (!c.isDeleted) return true;

                        // If deleted but has deletedAt, check if selected year is before or equal to deletion year
                        if (c.isDeleted && c.deletedAt) {
                          const deletedYear = dayjs(c.deletedAt).year();
                          return selectedYear <= deletedYear;
                        }

                        // If deleted without deletedAt (legacy), hide it
                        return false;
                      })
                      .forEach(col => {
                        (col.holidays || [])
                          .filter(h => {
                            // If not deleted, show the holiday
                            if (!h.isDeleted) return true;

                            // If deleted but has deletedAt, check if selected year is before or equal to deletion year
                            if (h.isDeleted && h.deletedAt) {
                              const deletedYear = dayjs(h.deletedAt).year();
                              return selectedYear <= deletedYear;
                            }

                            // If deleted without deletedAt (legacy), hide it
                            return false;
                          })
                          .forEach(h => {
                            const hDate = dayjs(h.date);
                            let displayDate = null;

                            if (hDate.year() === selectedYear) {
                              // Check if deleted before the holiday date
                              const notDeletedBeforeHoliday = !h.deletedAt ||
                                dayjs(h.deletedAt).isAfter(hDate, 'day') ||
                                dayjs(h.deletedAt).isSame(hDate, 'day');

                              if (notDeletedBeforeHoliday) {
                                displayDate = hDate;
                              }
                            } else if (h.isRepeat) {
                              // For repeating holidays, calculate the repeating date for this year
                              const repeatingDate = hDate.year(selectedYear);
                              const holidayStartYear = hDate.year();

                              // Only show if selectedYear >= date year
                              if (selectedYear >= holidayStartYear) {
                                // Check if created before the repeating date
                                const createdBeforeHoliday = !h.createdAt || dayjs(h.createdAt).isBefore(repeatingDate, 'day') || dayjs(h.createdAt).isSame(repeatingDate, 'day');

                                // Check if not deleted before the repeating date
                                const notDeletedBeforeHoliday = !h.deletedAt || dayjs(h.deletedAt).isAfter(repeatingDate, 'day') || dayjs(h.deletedAt).isSame(repeatingDate, 'day');

                                if (createdBeforeHoliday && notDeletedBeforeHoliday) {
                                  displayDate = repeatingDate;
                                }
                              }
                            }

                            if (displayDate) {
                              yearHolidays.push({
                                ...h,
                                displayDate: displayDate.format('YYYY-MM-DD'),
                                displayDateObj: displayDate,
                                collectionName: col.name
                              });
                            }
                          });
                      });

                    yearHolidays.sort((a, b) => dayjs(a.displayDate).valueOf() - dayjs(b.displayDate).valueOf());

                    // Stats Summary for selected year
                    const totalHolidays = yearHolidays.length;
                    const repeatingCount = yearHolidays.filter(h => h.isRepeat).length;
                    const floatingCount = yearHolidays.filter(h => h.isFloating).length;

                    return (
                      <Box>
                        {/* Stats Cards - Outlined Style */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={4}>
                            <Card variant="outlined">
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                                <Typography variant="h5" fontWeight="bold" color="primary">{totalHolidays}</Typography>
                                <Typography variant="caption" color="text.secondary">Total</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={4}>
                            <Card variant="outlined">
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                                <Typography variant="h5" fontWeight="bold" color="success.main">{repeatingCount}</Typography>
                                <Typography variant="caption" color="text.secondary">Static Holidays</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={4}>
                            <Card variant="outlined">
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                                <Typography variant="h5" fontWeight="bold" color="secondary.main">{floatingCount}</Typography>
                                <Typography variant="caption" color="text.secondary">Floating</Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {yearHolidays.length === 0 ? (
                          <Alert severity="info" variant="outlined">
                            No holidays for {selectedYear}. Add holidays or mark as "Repeat Every Year".
                          </Alert>
                        ) : (
                          <List dense sx={{ maxHeight: 450, overflow: 'auto' }}>
                            {yearHolidays.map((h, i) => (
                              <ListItem
                                key={`${h.name}-${h.displayDate}-${i}`}
                                sx={{
                                  py: 0.75,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                {/* Date Badge */}
                                <Box sx={{
                                  mr: 1.5,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  minWidth: 40,
                                  borderRadius: 1,
                                  py: 0.25
                                }}>
                                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', lineHeight: 1, textTransform: 'uppercase' }}>
                                    {dayjs(h.displayDate).format('MMM')}
                                  </Typography>
                                  <Typography sx={{ fontSize: '1rem', fontWeight: 'bold', lineHeight: 1.2 }}>
                                    {dayjs(h.displayDate).format('D')}
                                  </Typography>
                                </Box>
                                {/* Holiday Name with chips */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>{h.name}</Typography>
                                  {h.isRepeat && (
                                    <Chip label="R" size="small" color="success" sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                  {h.isFloating && (
                                    <Chip label="F" size="small" color="secondary" sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />
                                  )}
                                </Box>
                                {/* Collection Name */}
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                                  {h.collectionName}
                                </Typography>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )
      }



      {/* Add/Edit Dialog */}
      <Dialog
        open={openHolidayDialog}
        onClose={() => setOpenHolidayDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Collection</InputLabel>
              <Select
                value={dialogCollectionId || ''}
                label="Collection"
                onChange={(e) => setDialogCollectionId(e.target.value)}
              >
                {collections.filter(c => !c.isDeleted).map(c => (
                  <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Holiday Name"
              placeholder="e.g. Independence Day"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              autoFocus
            />
            <TextField
              label="Date"
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dayjs().format('YYYY-MM-DD') }}
            />
            <TextField
              label="Description (Optional)"
              value={formDesc}
              onChange={(e) => setFormDesc(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formFloating}
                  onChange={(e) => setFormFloating(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Floating Date</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check this for holidays like Diwali/Pongal that change date every year.
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formRepeat}
                  onChange={(e) => setFormRepeat(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Repeat Every Year</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check this for fixed-date holidays like Independence Day or New Year.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {editingHoliday && (
            <Button color="error" onClick={() => handleDeleteHoliday(editingHoliday)} sx={{ mr: 'auto' }}>Delete</Button>
          )}
          <Button onClick={() => setOpenHolidayDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHoliday}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog
        open={openBulkUpdateDialog}
        onClose={() => setOpenBulkUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Update Floating Dates</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Update the dates for all holidays marked as "Floating" across your collections for the selected year.
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Target Year</InputLabel>
              <Select
                value={bulkYear}
                label="Target Year"
                onChange={(e) => setBulkYear(e.target.value)}
              >
                {[dayjs().year(), dayjs().year() + 1, dayjs().year() + 2].map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            {Object.keys(bulkInputs).length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Object.keys(bulkInputs).length > 0 && Object.keys(bulkInputs).every(name => bulkSelected[name])}
                      indeterminate={
                        Object.values(bulkSelected).some(v => v) &&
                        !Object.values(bulkSelected).every(v => v)
                      }
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const newSelected = {};
                        Object.keys(bulkInputs).forEach(name => {
                          newSelected[name] = checked;
                        });
                        setBulkSelected(newSelected);
                      }}
                    />
                  }
                  label={<Typography variant="body2" fontWeight="bold">Select All</Typography>}
                />
              </Box>
            )}

            {Object.keys(bulkInputs).length > 0 ? (
              Object.keys(bulkInputs).map(name => {
                return (
                  <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Checkbox
                      size="small"
                      checked={!!bulkSelected[name]}
                      onChange={(e) => setBulkSelected({ ...bulkSelected, [name]: e.target.checked })}
                    />
                    <Box sx={{ minWidth: 150 }}>
                      <Typography sx={{ fontWeight: 'medium' }}>{name}</Typography>
                    </Box>
                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      disabled={!bulkSelected[name]}
                      value={bulkInputs[name]}
                      onChange={(e) => setBulkInputs({ ...bulkInputs, [name]: e.target.value })}
                    />
                  </Box>
                );
              })
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No floating holidays found in your collections.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mark holidays as "Floating Date" to see them here.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkUpdateDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!Object.values(bulkSelected).some(v => v)}
            onClick={async () => {
              let updatedCount = 0;
              let createdCount = 0;
              const changedCollectionsMap = {}; // Map<id, collection>

              const getMutableCol = (col) => {
                if (!changedCollectionsMap[col._id]) {
                  changedCollectionsMap[col._id] = { ...col, holidays: [...(col.holidays || [])] };
                }
                return changedCollectionsMap[col._id];
              };

              // 1. Identify which holidays need updating vs creating
              const namesToProcess = Object.keys(bulkInputs).filter(name => bulkSelected[name]);

              namesToProcess.forEach(name => {
                const newDate = bulkInputs[name];
                if (!newDate) return;

                // Check if this holiday already exists in the target year in ANY collection
                let targetInstanceFound = false;

                // We iterate over ORIGINAL collections to find matches, but modify the MUTABLE copy in map
                collections.forEach(col => {
                  if (col.isDeleted) return;

                  // Check matches in this collection
                  const colHolidays = col.holidays || [];
                  const needsUpdate = colHolidays.some(hol =>
                    !hol.isDeleted && hol.isFloating && hol.name === name && dayjs(hol.date).year() === parseInt(bulkYear)
                  );

                  if (needsUpdate) {
                    const mutableCol = getMutableCol(col);
                    mutableCol.holidays = mutableCol.holidays.map(h => {
                      if (h.isDeleted || !h.isFloating || h.name !== name) return h;
                      if (dayjs(h.date).year() === parseInt(bulkYear)) {
                        targetInstanceFound = true;
                        updatedCount++;
                        return { ...h, date: newDate };
                      }
                      return h;
                    });
                  }
                });

                // 2. If NOT found in target year, AUTO-CREATE in its original collection
                if (!targetInstanceFound) {
                  // Find the "source" version (most recent version of this holiday)
                  let sourceHoliday = null;
                  let sourceColId = null;

                  collections.forEach(col => {
                    if (col.isDeleted) return;
                    const match = (col.holidays || []).find(h => !h.isDeleted && h.name === name);
                    if (match) {
                      if (!sourceHoliday || dayjs(match.date).isAfter(sourceHoliday.date)) {
                        sourceHoliday = match;
                        sourceColId = col._id;
                      }
                    }
                  });

                  if (sourceHoliday && sourceColId) {
                    // Get the target collection to add to
                    const targetCol = collections.find(c => c._id === sourceColId);
                    if (targetCol) {
                      const mutableCol = getMutableCol(targetCol);
                      mutableCol.holidays.push({
                        ...sourceHoliday,
                        _id: undefined, // Backend will assign ID
                        date: newDate,
                        isDeleted: false
                      });
                      createdCount++;
                    }
                  }
                }
              });

              if (onUpdateCollection) {
                try {
                  const promises = Object.values(changedCollectionsMap).map(col => onUpdateCollection(col));
                  await Promise.all(promises);

                  setOpenBulkUpdateDialog(false);
                  const total = updatedCount + createdCount;
                  handleOpenDialog({
                    type: 'alert',
                    title: 'Bulk Update Complete',
                    message: total > 0
                      ? `Successfully managed ${total} holidays. (${updatedCount} updated, ${createdCount} newly created for ${bulkYear})`
                      : 'No holidays were affected.'
                  });
                } catch (e) {
                  console.error(e);
                  handleOpenDialog({ type: 'alert', title: 'Error', message: 'Failed to update floating holidays.' });
                }
              }
            }}
          >
            Update Selected
          </Button>
        </DialogActions>
      </Dialog>


      {/* Generic Feedback/Input Dialog */}
      <Dialog open={dialogConfig.open} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogConfig.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogConfig.message}</Typography>
          {dialogConfig.type === 'prompt' && (
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              variant="outlined"
              value={dialogConfig.inputValue}
              onChange={(e) => setDialogConfig(prev => ({ ...prev, inputValue: e.target.value }))}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {dialogConfig.type !== 'alert' && (
            <Button onClick={handleCloseDialog}>Cancel</Button>
          )}
          <Button onClick={handleDialogConfirm} variant="contained" color="primary">
            {dialogConfig.type === 'alert' ? 'OK' : (dialogConfig.confirmLabel || 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Help Note */}
      <Box sx={{ mt: 4, px: 2, pb: 2 }}>
        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Holiday Planner Tips:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0, '& li': { fontSize: '0.85rem', mb: 0.5 } }}>
            <li><strong>Collections:</strong> Create multiple collections (e.g., "National Holidays", "Company Holidays") to group your dates.</li>
            <li><strong>Static vs Floating:</strong> Use <em>Repeat Every Year</em> for fixed dates (e.g., New Year's Day). Use <em>Floating Date</em> for holidays that change dates annually (e.g., Diwali, Easter).</li>
            <li><strong>Bulk Updates:</strong> For Floating holidays, use the <strong>Update Floating Dates</strong> button to easily shift them to the next year without editing each one individually.</li>
          </Box>
        </Alert>
      </Box>

    </Box >
  );
};

export default HolidayPlanner;